import { DataGridProps } from 'react-data-grid';

import formatCurrency from '../../../core/support/formatCurrency';
import { getRedemptionAmount } from '../../../core/support/getRedemptionAmount';
import { GetFeesAndSales } from './__generated__/GetFeesAndSales';

export function makeRow(input: GetFeesAndSales & { vendorId: string }) {
  const { vendorId } = input;
  const flatFees = input.flatFees ?? [];
  const pctByCategoryFees = input.pctByCategoryFees ?? [];
  const redemptions = input.redemptions ?? [];
  const vendors = input.season?.vendors ?? [];
  const selectedVendor = vendors.find((vendor) => vendor.id === vendorId);
  const vendorSales = input.vendorSales ?? [];
  const selectedVendorSales = vendorSales.find(
    (item) => item.vendor === vendorId,
  );
  if (!selectedVendor) {
    return null;
  }
  const { name: vendorName, categoryOptions } = selectedVendor;
  const firstCategory = categoryOptions[0];
  if (!firstCategory) {
    return null;
  }
  const categoryPct = pctByCategoryFees.find(
    (item) => item.categoryId === firstCategory.id,
  );
  if (!categoryPct) {
    return null;
  }
  const percentage = selectedVendorSales?.percentage ?? categoryPct.percentage;
  const salesAmountInCents = selectedVendorSales?.salesAmountInCents ?? 0;
  const vendorFlatFees = flatFees.map((flatFee) => {
    const vendorFlatFee = selectedVendorSales?.flatFeesById.find(
      (item) => item.itemId === flatFee.id,
    );
    return {
      feeId: flatFee.id,
      amount: vendorFlatFee?.amountInCents ?? 0,
    };
  });
  const vendorRedemptions = redemptions.map((redemption) => {
    const vendorRedemption = selectedVendorSales?.redemptionsById.find(
      (item) => item.itemId === redemption.id,
    );
    return {
      redemptionId: redemption.id,
      amountInCents: vendorRedemption ? vendorRedemption.amountInCents : 0,
    };
  });
  const getSalesTotal = (input: {
    salesAmountInCents: number;
    vendorRedemptions: Array<{
      redemptionId: string;
      amountInCents: number;
    }>;
  }) => {
    const { salesAmountInCents, vendorRedemptions } = input;
    const salesTotal =
      salesAmountInCents +
      vendorRedemptions.reduce((prev, curr) => {
        const addAbsValueToSalesTotal =
          redemptions.find((redemption) => curr.redemptionId === redemption.id)
            ?.addAbsValueToSalesTotal ?? false;
        return addAbsValueToSalesTotal
          ? prev + Math.abs(curr.amountInCents)
          : prev;
      }, 0);
    return salesTotal;
  };
  const salesTotal = getSalesTotal({ salesAmountInCents, vendorRedemptions });
  const getPctOfSales = (input: { percentage: number; salesTotal: number }) => {
    const { percentage, salesTotal } = input;
    const pctOfSales = salesTotal * (percentage / 100);
    return pctOfSales;
  };
  const pctOfSales = getPctOfSales({ percentage, salesTotal });
  const getInvoiceTotal = (input: {
    pctOfSales: number;
    vendorFlatFeesSum: number;
    vendorRedemptionsSum: number;
  }) => {
    const { pctOfSales, vendorFlatFeesSum, vendorRedemptionsSum } = input;
    const invoiceTotal = pctOfSales + vendorFlatFeesSum + vendorRedemptionsSum;
    return invoiceTotal;
  };
  const vendorFlatFeesSum = vendorFlatFees.reduce(
    (prev, curr) => prev + curr.amount,
    0,
  );
  const vendorRedemptionsSum = vendorRedemptions.reduce((prev, curr) => {
    const redemption = redemptions.find(
      (redemption) => redemption.id === curr.redemptionId,
    );
    const amountToAddToInvoice = getRedemptionAmount({
      amountInCents: curr.amountInCents,
      redemption,
    });
    return prev + amountToAddToInvoice;
  }, 0);
  const invoiceTotal = getInvoiceTotal({
    pctOfSales,
    vendorFlatFeesSum,
    vendorRedemptionsSum,
  });

  return {
    vendorName,
    vendorId,
    flatFeeTotal: vendorFlatFeesSum,
    sales: formatCurrency(salesAmountInCents),
    salesId: selectedVendorSales?.id,
    category: firstCategory,
    percentage,
    pctOfSales,
    flatFees: Object.fromEntries(
      vendorFlatFees.map((fee) => [fee.feeId, fee.amount]),
    ),
    redemptions: Object.fromEntries(
      vendorRedemptions.map((redemption) => [
        redemption.redemptionId,
        formatCurrency(redemption.amountInCents),
      ]),
    ),
    invoiceTotal,
    salesTotal,
    salesNoteCount: selectedVendorSales?.salesNoteCount ?? 0,
  };
}

export type Row = NonNullable<ReturnType<typeof makeRow>>;
export type Rows = Array<Row>;
export type Columns = DataGridProps<Row>['columns'];
